import React, { useMemo } from "react";
import { SectionBasedProduct } from "@brandclub/common-ui";
import {
  ConversationMessage,
  DisplayMessage,
  ProductPreview,
  TypingMessage,
  StreamingMessage,
} from "../types/conversation";
import { ChatMessage } from "./ChatMessage";

interface MessageListV2Props {
  conversationId: string | null;
  messages: ConversationMessage[];
  showTypingIndicator: boolean;
  streamingAIResponse: string;
  productMetadataMap: Map<string, SectionBasedProduct>;
  streamingProductGroups: Map<number, string[]>;
  currentStatus: string;
  previewSearchQueries: string[];
  previewQueryProducts: Array<{
    query: string;
    queryIndex: number;
    products: ProductPreview[];
    totalResults: number;
  }>;
  isWaitingForFollowUpQueries: boolean;
  isActivelyStreaming: boolean;
  streamingFeaturedProduct: {
    product: SectionBasedProduct;
    question: string;
  } | null;
  onSearch: (query: string) => void;
  onRetry?: (messageIndex: number) => Promise<void>;
  onUpdateFeedback?: (
    messageIndex: number,
    feedback: { type: "like" | "dislike"; timestamp: string } | null
  ) => void;
  bottomRef: React.RefObject<HTMLDivElement>;
}

export const MessageListV2: React.FC<MessageListV2Props> = ({
  conversationId,
  messages,
  showTypingIndicator,
  streamingAIResponse,
  productMetadataMap,
  streamingProductGroups,
  currentStatus,
  previewSearchQueries,
  previewQueryProducts,
  isWaitingForFollowUpQueries,
  isActivelyStreaming,
  streamingFeaturedProduct,
  bottomRef,
  onSearch,
  onRetry,
  onUpdateFeedback,
}) => {
  // Combine all messages for display
  const displayMessages: DisplayMessage[] = useMemo(
    () => [
      ...messages,
      ...(showTypingIndicator
        ? [
            {
              role: "assistant" as const,
              content: "",
              timestamp: new Date(),
              isTyping: true,
            } as TypingMessage,
          ]
        : []),
      // Add a streaming message if:
      // 1. We have streaming AI response content, OR
      // 2. We're actively streaming and have preview data (even if no AI response yet)
      ...(streamingAIResponse ||
      (isActivelyStreaming &&
        (previewSearchQueries.length > 0 || previewQueryProducts.length > 0))
        ? [
            {
              role: "assistant" as const,
              content: streamingAIResponse,
              timestamp: new Date(),
              isStreaming: true,
            } as StreamingMessage,
          ]
        : []),
    ],
    [
      messages,
      showTypingIndicator,
      streamingAIResponse,
      isActivelyStreaming,
      previewSearchQueries.length,
      previewQueryProducts.length,
    ]
  );

  return (
    <div
      style={{
        width: "100%",
        maxWidth: "900px",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      {displayMessages.map((message, displayIndex) => {
        const hasStreamingMessage =
          !!streamingAIResponse ||
          (isActivelyStreaming &&
            (previewSearchQueries.length > 0 ||
              previewQueryProducts.length > 0));
        const isLastMessage =
          displayIndex ===
          messages.length +
            (showTypingIndicator ? 1 : 0) +
            (hasStreamingMessage ? 1 : 0) -
            1;
        const isTyping = "isTyping" in message && message.isTyping;
        const isStreaming = "isStreaming" in message && message.isStreaming;

        // Show streaming featured product on the streaming assistant message
        const showStreamingFeaturedProduct =
          isStreaming && streamingFeaturedProduct;

        // For streaming messages, use conversation-level preview data
        // For regular messages, use message-specific preview data if available
        const messageHasPreviewData =
          !isStreaming &&
          !isTyping &&
          "previewSearchQueries" in message &&
          message.previewSearchQueries &&
          message.previewSearchQueries.length > 0;

        const shouldShowPreviewOnThisMessage =
          isStreaming || messageHasPreviewData;

        // Generate a stable key that doesn't change when streaming becomes a regular message
        // For typing indicator, use a special key
        // For streaming messages, use the next message index (since they'll become a regular message)
        // For regular messages, use their index in the messages array
        const messageKey = isTyping
          ? "typing-indicator"
          : isStreaming
          ? `message-${messages.length}` // Use the index where this message will be added
          : `message-${displayIndex}`;

        return (
          <>
            <ChatMessage
              bottomRef={isLastMessage ? bottomRef : undefined}
              key={messageKey}
              role={message.role}
              content={message.content}
              isTyping={isTyping}
              isStreaming={isStreaming}
              isLastMessage={isLastMessage}
              conversationId={conversationId || undefined}
              messageIndex={
                "messageIndex" in message ? message.messageIndex : undefined
              }
              feedback={"feedback" in message ? message.feedback : undefined}
              followUpQueries={[]} // Don't render inline follow-up queries in V2 mode - they're rendered at the bottom
              productMetadataMap={productMetadataMap}
              streamingProductGroups={streamingProductGroups}
              currentStatus={isLastMessage ? currentStatus : undefined}
              previewSearchQueries={
                isStreaming && isLastMessage
                  ? previewSearchQueries
                  : "previewSearchQueries" in message
                  ? message.previewSearchQueries || []
                  : []
              }
              previewQueryProducts={
                isStreaming && isLastMessage
                  ? previewQueryProducts
                  : "previewQueryProducts" in message
                  ? message.previewQueryProducts || []
                  : []
              }
              isWaitingForFollowUpQueries={
                isLastMessage ? isWaitingForFollowUpQueries : false
              }
              featuredProduct={
                "featuredProduct" in message
                  ? message.featuredProduct
                  : undefined
              }
              streamingFeaturedProduct={
                showStreamingFeaturedProduct
                  ? streamingFeaturedProduct
                  : undefined
              }
              onSearch={onSearch}
              onRetry={onRetry}
              onUpdateFeedback={onUpdateFeedback}
              // bottomRef={isLastMessage ? bottomRef : undefined}
            />
          </>
        );
      })}
    </div>
  );
};
